import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../../api';
import { Loading, ErrorState, Empty, fmtDate } from '../../components/ui';

/* ============================================================
   Programme — impor, tinjau, terima.

   Halaman ini punya satu pekerjaan yang benar-benar penting:
   membuat selisih terbaca SEBELUM sebuah update diterima. Karena
   itu daftar selisih dibuka otomatis begitu sebuah versi dipilih,
   dan tombol Terima duduk di bawahnya, bukan di sebelahnya.
   ============================================================ */

type Version = {
  id: string;
  label: string;
  dataDate: string;
  source: string;
  isBaseline: boolean;
  status: 'DRAFT' | 'ACCEPTED' | 'REJECTED';
  activityCount: number;
  createdAt: string;
  reviewedAt: string | null;
  importedBy: { name: string } | null;
  _count: { diffs: number };
};

type DiffLine = {
  kind: string;
  finishShiftDays: number;
  durationDelta: number;
  floatDelta: number;
  pctDelta: number;
  wasCritical: boolean;
  isCritical: boolean;
  activity: { activityCode: string; name: string };
};

const KIND_LABEL: Record<string, string> = {
  ADDED: 'Baru',
  REMOVED: 'Hilang',
  DATE_SHIFTED: 'Tanggal bergeser',
  DURATION_CHANGED: 'Durasi berubah',
  FLOAT_CHANGED: 'Float berubah',
  PROGRESS_CHANGED: 'Progres berubah',
};

const STATUS_CHIP: Record<string, string> = {
  DRAFT: 'warn',
  ACCEPTED: 'good',
  REJECTED: 'crit',
};

export function Programme({ projectId }: { projectId: string }) {
  const [versions, setVersions] = useState<Version[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [diff, setDiff] = useState<{ lines: DiffLine[]; counts: Record<string, number> } | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get<{ versions: Version[] }>(`/programme/versions?projectId=${projectId}`);
      setVersions(r.versions);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load programme versions.');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!selected) return setDiff(null);
    void api
      .get<{ lines: DiffLine[]; counts: Record<string, number> }>(`/programme/versions/${selected}/diff`)
      .then(setDiff)
      .catch(() => setDiff(null));
  }, [selected]);

  async function review(id: string, decision: 'ACCEPTED' | 'REJECTED') {
    const note =
      decision === 'REJECTED'
        ? window.prompt('Alasan penolakan — wajib diisi, dan ikut tersimpan:')
        : window.prompt('Catatan (opsional):') ?? '';
    if (decision === 'REJECTED' && !note?.trim()) return;

    setBusy(true);
    try {
      await api.post(`/programme/versions/${id}/review`, { decision, note });
      setNotice(decision === 'ACCEPTED' ? 'Versi diterima.' : 'Versi ditolak; alasannya tersimpan.');
      await load();
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : 'Gagal.');
    } finally {
      setBusy(false);
    }
  }

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!versions) return <Loading rows={4} />;

  const current = versions.find((v) => v.id === selected) ?? null;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Programme</h1>
          <p>
            Munin membaca programme, tidak menjadwal ulang. Float dan lintasan kritis datang dari
            berkasnya.
          </p>
        </div>
      </div>

      <ImportPanel projectId={projectId} onDone={load} />

      {notice && <div className="banner">{notice}</div>}

      {versions.length === 0 ? (
        <Empty
          title="Belum ada programme"
          body="Impor baseline dari P6, MS Project, atau CSV untuk memulai. Semua modul lain menggantung pada ini."
        />
      ) : (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Versi</th>
                <th>Data date</th>
                <th>Sumber</th>
                <th>Aktivitas</th>
                <th>Selisih</th>
                <th>Status</th>
                <th>Diimpor</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr
                  key={v.id}
                  onClick={() => setSelected(selected === v.id ? null : v.id)}
                  className={selected === v.id ? 'on' : undefined}
                >
                  <td className="num">
                    {v.label} {v.isBaseline && <span className="chip blue">Baseline</span>}
                  </td>
                  <td>{fmtDate(v.dataDate)}</td>
                  <td>{v.source.replace('_', ' ')}</td>
                  <td>{v.activityCount}</td>
                  <td>{v._count.diffs || '—'}</td>
                  <td>
                    <span className={`chip ${STATUS_CHIP[v.status] ?? ''}`}>{v.status}</span>
                  </td>
                  <td>{v.importedBy?.name ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {current && (
        <section className="panel panel-wide" style={{ marginTop: 20 }}>
          <header className="panel-head">
            <h3>Apa yang berubah — {current.label}</h3>
            <p>
              Baca ini sebelum menerima. Update yang diterima tanpa dibaca adalah cara paling umum
              tanggal bergeser tanpa ada yang menyadarinya.
            </p>
          </header>

          {!diff ? (
            <Loading rows={3} />
          ) : diff.lines.length === 0 ? (
            <p className="hint">
              Tidak ada baris selisih. Versi pertama tidak punya pembanding, dan itu bukan hal yang
              sama dengan tidak ada perubahan.
            </p>
          ) : (
            <>
              <div className="legend" style={{ marginBottom: 14 }}>
                {Object.entries(diff.counts).map(([k, n]) => (
                  <span key={k} className="legend-item">
                    <span className="legend-name">{KIND_LABEL[k] ?? k}</span>
                    <b>{n}</b>
                  </span>
                ))}
              </div>

              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Aktivitas</th>
                      <th>Perubahan</th>
                      <th>Geser</th>
                      <th>Float</th>
                      <th>Durasi</th>
                      <th>Kritis</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diff.lines.slice(0, 60).map((l, i) => (
                      <tr key={l.activity.activityCode + i} className="no-click">
                        <td className="num">
                          {l.activity.activityCode}
                          <div className="hint">{l.activity.name}</div>
                        </td>
                        <td>{KIND_LABEL[l.kind] ?? l.kind}</td>
                        <td className={l.finishShiftDays > 0 ? 'neg' : l.finishShiftDays < 0 ? 'pos' : ''}>
                          {l.finishShiftDays === 0 ? '—' : `${l.finishShiftDays > 0 ? '+' : ''}${l.finishShiftDays} h`}
                        </td>
                        <td>{l.floatDelta === 0 ? '—' : `${l.floatDelta > 0 ? '+' : ''}${l.floatDelta} h`}</td>
                        <td>{l.durationDelta === 0 ? '—' : `${l.durationDelta > 0 ? '+' : ''}${l.durationDelta} h`}</td>
                        <td>
                          {l.isCritical ? (
                            <span className="chip crit">Kritis</span>
                          ) : l.wasCritical ? (
                            <span className="chip">Tidak lagi</span>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {diff.lines.length > 60 && (
                <p className="hint" style={{ marginTop: 10 }}>
                  Menampilkan 60 dari {diff.lines.length} baris, yang kritis lebih dulu.
                </p>
              )}
            </>
          )}

          {current.status === 'DRAFT' && (
            <div className="dialog-actions" style={{ marginTop: 18 }}>
              <button className="btn btn-ghost" disabled={busy} onClick={() => review(current.id, 'REJECTED')}>
                Tolak
              </button>
              <button className="btn btn-primary" disabled={busy} onClick={() => review(current.id, 'ACCEPTED')}>
                Terima versi ini
              </button>
            </div>
          )}
        </section>
      )}
    </>
  );
}

/* ------------------------------------------------------------ */

function ImportPanel({ projectId, onDone }: { projectId: string; onDone: () => void }) {
  const [label, setLabel] = useState('');
  const [dataDate, setDataDate] = useState('');
  const [baseline, setBaseline] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file || !label.trim()) return;

    setBusy(true);
    setResult(null);
    setWarnings([]);
    try {
      const text = await file.text();
      const q = new URLSearchParams({ projectId, label: label.trim() });
      if (dataDate) q.set('dataDate', dataDate);
      if (baseline) q.set('baseline', 'true');

      const r = await api.uploadProgramme(q.toString(), text);
      setResult(
        `${r.activities} aktivitas terbaca. ${r.diff.baseline ? 'Versi pertama — tidak ada pembanding.' : r.diff.summary}`,
      );
      setWarnings(r.warnings);
      setLabel('');
      if (fileRef.current) fileRef.current.value = '';
      onDone();
    } catch (err) {
      setResult(err instanceof ApiError ? err.message : 'Impor gagal.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel" style={{ marginBottom: 20 }}>
      <header className="panel-head">
        <h3>Impor versi</h3>
        <p>P6 XER, MS Project XML, atau CSV. Formatnya dikenali dari isi berkas.</p>
      </header>

      <form onSubmit={submit}>
        <div className="filters">
          <input
            className="input"
            placeholder="Nama versi — misalnya Update 12"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            required
          />
          <input
            className="input"
            type="date"
            title="Data date — hanya perlu diisi kalau berkasnya tidak menyebutkan"
            value={dataDate}
            onChange={(e) => setDataDate(e.target.value)}
          />
          <input className="input" type="file" ref={fileRef} accept=".xer,.xml,.csv,text/*" required />
          <label className="hint" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={baseline} onChange={(e) => setBaseline(e.target.checked)} />
            Baseline
          </label>
          <button className="btn btn-primary" disabled={busy}>
            {busy ? 'Membaca…' : 'Impor'}
          </button>
        </div>
      </form>

      {result && <div className="banner">{result}</div>}
      {warnings.length > 0 && (
        <ul className="hint" style={{ marginTop: 10 }}>
          {warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}
    </section>
  );
}
